import React, { useContext, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CAlert, CButton, CCard, CCardBody, CCardHeader, CCardTitle, CCol, CFormInput,
  CFormSelect, CModal, CModalBody, CModalFooter, CModalHeader, CModalTitle, CRow,
  CSpinner, CTable, CTableBody, CTableDataCell, CTableHead, CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'

import { api, ApiError } from '../api/client'
import { ROLES } from '../auth/capabilities'
import { AuthContext } from '../App'

interface Invite { id: number; email: string; created_at: string; expires_at: string; used: boolean }
interface AccessEntry {
  email: string
  role: string
  activated: boolean
  invited: boolean
  user_id: number | null
}
interface PersonRow {
  id: number
  name: string
  nickname: string
  group_name: string
}

const NicknamesCard: React.FC = () => {
  const queryClient = useQueryClient()
  const people = useQuery({
    queryKey: ['people'],
    queryFn: () => api.get<PersonRow[]>('/v1/people'),
  })
  const [draft, setDraft] = useState<Record<number, string>>({})
  const [savedFlash, setSavedFlash] = useState(false)

  useEffect(() => {
    // Seed drafts for people not seen before; never clobber in-progress edits.
    setDraft((prev) => {
      const next = { ...prev }
      for (const p of people.data ?? []) {
        if (!(p.id in next)) next[p.id] = p.nickname ?? ''
      }
      return next
    })
  }, [people.data])

  const rows = people.data ?? []
  const dirty = rows.filter((p) => draft[p.id] !== undefined && draft[p.id] !== (p.nickname ?? ''))

  const save = useMutation({
    mutationFn: async () => {
      for (const p of dirty) {
        await api.put(`/v1/admin/people/${p.id}/nickname`, { nickname: draft[p.id] })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['people'] })
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 2000)
    },
  })

  const setNickname = (id: number, v: string) =>
    setDraft((prev) => ({ ...prev, [id]: v }))

  return (
    <CCard className="mb-4">
      <CCardHeader>
        <CCardTitle className="mb-0">Nicknames</CCardTitle>
      </CCardHeader>
      <CCardBody>
        <p className="text-body-secondary small">
          A nickname is shown instead of the person's first name everywhere on the site
          (including the shared calendar feed). Leave it blank to use the first name.
        </p>
        {people.isLoading ? (
          <div className="text-center py-3"><CSpinner /></div>
        ) : people.isError ? (
          <CAlert color="danger">Couldn't load people. Refresh the page.</CAlert>
        ) : rows.length === 0 ? (
          <p className="text-body-secondary">No people yet — add them under 👥 People first.</p>
        ) : (
          <>
            {save.isError && (
              <CAlert color="danger">
                {save.error instanceof ApiError ? save.error.message : 'Save failed. Try again.'}
              </CAlert>
            )}
            <div className="table-responsive">
              <CTable hover align="middle" small>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Traveler</CTableHeaderCell>
                    <CTableHeaderCell>Group</CTableHeaderCell>
                    <CTableHeaderCell style={{ width: '40%' }}>Nickname</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {rows.map((person) => (
                    <CTableRow key={person.id}>
                      <CTableDataCell>{person.name}</CTableDataCell>
                      <CTableDataCell className="small text-body-secondary">
                        {person.group_name}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CFormInput
                          size="sm"
                          aria-label={`Nickname for ${person.name}`}
                          placeholder="— first name —"
                          value={draft[person.id] ?? ''}
                          onChange={(e) => setNickname(person.id, e.target.value)}
                        />
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </div>
            <div className="d-flex align-items-center gap-2">
              <CButton
                color="primary"
                disabled={dirty.length === 0 || save.isPending}
                onClick={() => save.mutate()}
              >
                {save.isPending ? <CSpinner size="sm" /> : 'Save changes'}
              </CButton>
              {dirty.length > 0 && (
                <span className="small text-body-secondary">
                  {dirty.length} unsaved change{dirty.length === 1 ? '' : 's'}
                </span>
              )}
              {save.isSuccess && dirty.length === 0 && savedFlash && (
                <span className="small text-success">✓ Saved</span>
              )}
            </div>
          </>
        )}
      </CCardBody>
    </CCard>
  )
}

const Admin: React.FC = () => {
  const queryClient = useQueryClient()
  const { auth } = useContext(AuthContext)
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [newUrl, setNewUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [copiedId, setCopiedId] = useState<number | null>(null)

  const invites = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => api.get<Invite[]>('/v1/admin/invites'),
  })
  const access = useQuery({
    queryKey: ['admin', 'access'],
    queryFn: () => api.get<AccessEntry[]>('/v1/admin/access'),
  })

  const createInvite = useMutation({
    mutationFn: (e: string) => api.post<{ url: string }>('/v1/admin/invites', { email: e }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      setNewUrl(data.url)
      setEmail('')
      setError(null)
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create invite.')
    },
  })

  const revoke = useMutation({
    mutationFn: (id: number) => api.delete(`/v1/admin/invites/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  })

  const copyLink = useMutation({
    mutationFn: async (id: number) => {
      const data = await api.post<{ url: string }>(`/v1/admin/invites/${id}/url`)
      try {
        await navigator.clipboard.writeText(data.url)
      } catch {
        window.prompt('Copy this URL:', data.url)
      }
      return data.url
    },
    onSuccess: (_url, id) => {
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    },
    onError: (err) => {
      window.alert(err instanceof ApiError ? err.message : 'Failed to copy link.')
    },
  })

  const setRole = useMutation({
    mutationFn: (p: { id: number; role: string }) =>
      api.put(`/v1/admin/users/${p.id}/role`, { role: p.role }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
    onError: (err) => {
      window.alert(err instanceof ApiError ? err.message : 'Failed to change role.')
      queryClient.invalidateQueries({ queryKey: ['admin'] })
    },
  })

  const copyUrl = async () => {
    if (!newUrl) return
    try {
      await navigator.clipboard.writeText(newUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      window.prompt('Copy this URL:', newUrl)
    }
  }

  const onInvite = (e: React.FormEvent) => {
    e.preventDefault()
    if (email.trim()) createInvite.mutate(email.trim())
  }

  return (
    <>
      <h3 className="mb-3">⚙️ Admin</h3>
      {invites.isError && (
        <CAlert color="danger">Admin endpoints unavailable. Are you signed in as admin?</CAlert>
      )}

      <CRow>
        <CCol xl={7} className="mb-4">
          <CCard className="mb-4">
            <CCardHeader><CCardTitle className="mb-0">Invite links</CCardTitle></CCardHeader>
            <CCardBody>
              <p className="text-body-secondary small">
                Create a link, then text it to that person. Links are their permanent
                login (bookmarkable). Copy a link again anytime someone loses theirs;
                revoke if one leaks.
              </p>
              <form className="row g-2 mb-3" onSubmit={onInvite}>
                <div className="col">
                  <CFormInput
                    type="email"
                    placeholder="any email, e.g. friend@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="col-auto">
                  <CButton color="primary" type="submit" disabled={createInvite.isPending}>
                    {createInvite.isPending ? <CSpinner size="sm" /> : 'Create invite'}
                  </CButton>
                </div>
              </form>
              {error && <CAlert color="danger">{error}</CAlert>}

              {invites.isLoading ? (
                <div className="text-center py-3"><CSpinner /></div>
              ) : (
                <div className="table-responsive">
                  <CTable hover align="middle" small>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>Email</CTableHeaderCell>
                        <CTableHeaderCell>Created</CTableHeaderCell>
                        <CTableHeaderCell>Status</CTableHeaderCell>
                        <CTableHeaderCell className="text-end">Actions</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {(invites.data ?? []).map((i) => (
                        <CTableRow key={i.id}>
                          <CTableDataCell>{i.email}</CTableDataCell>
                          <CTableDataCell className="small">
                            {new Date(i.created_at).toLocaleDateString()}
                          </CTableDataCell>
                          <CTableDataCell>
                            {i.used ? '✅ activated' : '⏳ not yet'}
                          </CTableDataCell>
                          <CTableDataCell className="text-end">
                            <CButton
                              size="sm" color="primary" variant="outline" className="me-2"
                              disabled={copyLink.isPending}
                              onClick={() => copyLink.mutate(i.id)}
                            >
                              {copiedId === i.id ? '✓ Copied' : 'Copy link'}
                            </CButton>
                            <CButton
                              size="sm" color="danger" variant="outline"
                              disabled={revoke.isPending}
                              onClick={() => revoke.mutate(i.id)}
                            >
                              Revoke
                            </CButton>
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                </div>
              )}
            </CCardBody>
          </CCard>
        </CCol>

        <CCol xl={5} className="mb-4">
          <CCard className="h-100">
            <CCardHeader><CCardTitle className="mb-0">Trip access</CCardTitle></CCardHeader>
            <CCardBody>
              <p className="text-body-secondary small">
                Everyone who can sign in — anyone you've invited, plus the starter list from{' '}
                <code>CR_ALLOWED_EMAILS</code> on the server. Roles: <b>viewer</b> read-only ·{' '}
                <b>contributor</b> add anything, edit their own rows ·{' '}
                <b>editor</b> also delete anything · <b>admin</b> this page + reassign creators.
                New members start as contributors. Revoke an invite to cut off access.
              </p>
              {access.isLoading ? (
                <div className="text-center py-3"><CSpinner /></div>
              ) : access.isError ? (
                <CAlert color="danger">
                  Couldn't load the access list.{' '}
                  {access.error instanceof ApiError ? access.error.message : 'Try refreshing.'}
                </CAlert>
              ) : (access.data ?? []).length === 0 ? (
                <p className="text-body-secondary">Nobody yet — create the first invite.</p>
              ) : (
                <CTable hover align="middle" small>
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell>Email</CTableHeaderCell>
                      <CTableHeaderCell>Role</CTableHeaderCell>
                      <CTableHeaderCell>Status</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {(access.data ?? []).map((a) => (
                      <CTableRow key={a.email}>
                        <CTableDataCell>{a.email}</CTableDataCell>
                        <CTableDataCell>
                          {a.user_id != null ? (
                            <CFormSelect
                              size="sm"
                              className="w-auto"
                              aria-label={`Role for ${a.email}`}
                              value={a.role}
                              disabled={a.email === auth.user?.email || setRole.isPending}
                              title={a.email === auth.user?.email
                                ? "You can't change your own role" : undefined}
                              onChange={(e) =>
                                setRole.mutate({ id: a.user_id!, role: e.target.value })}
                            >
                              {ROLES.map((r) => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </CFormSelect>
                          ) : (
                            <span className="small text-body-secondary">{a.role} (default)</span>
                          )}
                        </CTableDataCell>
                        <CTableDataCell>
                          {a.activated ? '✅ signed in' : a.invited ? '⏳ invited' : '—'}
                        </CTableDataCell>
                      </CTableRow>
                    ))}
                  </CTableBody>
                </CTable>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <NicknamesCard />

      {/* New invite URL modal — shown once at creation (re-copyable later from the table) */}
      <CModal visible={!!newUrl} onClose={() => setNewUrl(null)}>
        <CModalHeader>
          <CModalTitle>Invite created — send it now</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="small text-body-secondary">
            Copy it and text it — this link stays their permanent login, and you can
            copy it again anytime from the table above.
          </p>
          <CAlert color="info" className="text-break" style={{ userSelect: 'all' }}>{newUrl}</CAlert>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" variant="outline" onClick={() => setNewUrl(null)}>
            Close
          </CButton>
          <CButton color="primary" onClick={copyUrl}>
            {copied ? '✓ Copied' : 'Copy link'}
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default Admin
